import React, { useState, useEffect, useMemo } from 'react';
import {
  getOrganizationsList,
  addNewOrganization,
  updateExistingOrganization,
  getOrganizationDetails,
  deleteOrganizationById,
} from '../services/api';
import '../styles/OrganizationList.css';

const emptyOrganizationForm = {
  organizationName: '',
  industry: '',
  companySize: '',
  businessModel: '',
  headquarters: '',
  contactDetails: {
    name: '',
    email: '',
    phone: '',
  },
  financialDetails: {
    annualRevenue: '',
    fundingStage: '',
    valuation: '',
  },
  businessDetails: {
    description: '',
    other_field: '',
  },
};

const getOrganizationId = (org) => org?._id || org?.id || org?.organization_id;

const normalizeOrganizationForm = (org = {}) => ({
  ...emptyOrganizationForm,
  ...org,
  contactDetails: {
    ...emptyOrganizationForm.contactDetails,
    ...(org.contactDetails || {}),
  },
  financialDetails: {
    ...emptyOrganizationForm.financialDetails,
    ...(org.financialDetails || {}),
  },
  businessDetails: {
    ...emptyOrganizationForm.businessDetails,
    ...(org.businessDetails || {}),
  },
});

const OrganizationList = () => {
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [form, setForm] = useState(emptyOrganizationForm);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const filteredOrganizations = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return organizations;

    return organizations.filter((org) => {
      const name = org.organizationName || '';
      const industry = org.industry || '';
      const headquarters = org.headquarters || '';
      return `${name} ${industry} ${headquarters}`.toLowerCase().includes(term);
    });
  }, [searchTerm, organizations]);

  const visibleOrganizationIds = useMemo(
    () => filteredOrganizations.map(getOrganizationId).filter(Boolean),
    [filteredOrganizations]
  );

  const allVisibleSelected =
    visibleOrganizationIds.length > 0 &&
    visibleOrganizationIds.every((id) => selectedIds.includes(id));

  const fetchOrganizations = async () => {
    try {
      const data = await getOrganizationsList();
      setOrganizations(Array.isArray(data) ? data : []);
      setSelectedIds([]);
      setError('');
    } catch (err) {
      console.error('Error fetching organizations:', err);
      setError('Failed to load organizations.');
      setOrganizations([]);
      setSelectedIds([]);
    }
  };

  const handleInputChange = (e, field, subfield) => {
    const { value } = e.target;
    if (subfield) {
      setForm((prev) => ({
        ...prev,
        [field]: {
          ...prev[field],
          [subfield]: value,
        },
      }));
      return;
    }

    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleEdit = async (id) => {
    try {
      const org = await getOrganizationDetails(id);
      setForm(normalizeOrganizationForm(org));
      setSelectedOrganization(org);
      setIsEditing(true);
      setIsModalOpen(true);
      setError('');
      setNotice('');
    } catch (err) {
      console.error('Error fetching organization details:', err);
      setError('Failed to fetch organization details.');
    }
  };

  const handleAddNew = () => {
    resetForm();
    setIsEditing(false);
    setIsModalOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      if (isEditing && selectedOrganization) {
        await updateExistingOrganization(getOrganizationId(selectedOrganization), form);
        setNotice('Organization updated successfully.');
      } else {
        await addNewOrganization(form);
        setNotice('Organization added successfully.');
      }
      await fetchOrganizations();
      closeModal();
    } catch (err) {
      console.error('Error saving organization:', err);
      setError('Failed to save organization.');
    }
  };

  const toggleSelection = (orgId) => {
    setSelectedIds((prev) =>
      prev.includes(orgId) ? prev.filter((id) => id !== orgId) : [...prev, orgId]
    );
  };

  const toggleSelectAllVisible = () => {
    setSelectedIds((prev) => {
      if (allVisibleSelected) {
        return prev.filter((id) => !visibleOrganizationIds.includes(id));
      }

      return Array.from(new Set([...prev, ...visibleOrganizationIds]));
    });
  };

  const handleDelete = async (org) => {
    const orgId = getOrganizationId(org);
    const name = org.organizationName || 'this organization';
    if (!orgId) return;

    if (!window.confirm(`Delete "${name}"? This action cannot be undone.`)) return;

    try {
      setIsDeleting(true);
      await deleteOrganizationById(orgId);
      setNotice('Organization deleted successfully.');
      await fetchOrganizations();
    } catch (err) {
      console.error('Error deleting organization:', err);
      setError(err.response?.data?.detail || 'Failed to delete organization.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;

    if (
      !window.confirm(
        `Delete ${selectedIds.length} selected organization${selectedIds.length > 1 ? 's' : ''}? This action cannot be undone.`
      )
    ) {
      return;
    }

    try {
      setIsDeleting(true);
      await Promise.all(selectedIds.map((id) => deleteOrganizationById(id)));
      setNotice(`${selectedIds.length} organization${selectedIds.length > 1 ? 's' : ''} deleted successfully.`);
      await fetchOrganizations();
    } catch (err) {
      console.error('Error bulk deleting organizations:', err);
      setError(err.response?.data?.detail || 'Failed to delete selected organizations.');
    } finally {
      setIsDeleting(false);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    resetForm();
  };

  const resetForm = () => {
    setForm(emptyOrganizationForm);
    setSelectedOrganization(null);
    setIsEditing(false);
    setError('');
  };

  return (
    <div className="organization-list">
      <div className="organization-hero">
        <div>
          <p className="organization-eyebrow">Admin control center</p>
          <h2>Organizations</h2>
          <p className="organization-subtitle">
            Manage company profiles, contact details, and interview ownership from one cleaner workspace.
          </p>
        </div>
        <button className="org-button org-button-primary" onClick={handleAddNew}>
          Add New Organization
        </button>
      </div>

      <div className="organization-stats" aria-label="Organization summary">
        <div className="organization-stat">
          <span>Total</span>
          <strong>{organizations.length}</strong>
        </div>
        <div className="organization-stat">
          <span>Visible</span>
          <strong>{filteredOrganizations.length}</strong>
        </div>
        <div className="organization-stat">
          <span>Selected</span>
          <strong>{selectedIds.length}</strong>
        </div>
      </div>

      {error && <p className="organization-alert organization-alert-error">{error}</p>}
      {notice && <p className="organization-alert organization-alert-success">{notice}</p>}

      <div className="organization-toolbar">
        <label className="organization-search">
          <span>Search organizations</span>
          <input
            type="text"
            placeholder="Search by name, industry, or headquarters"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </label>
        <button
          className="org-button org-button-danger"
          onClick={handleBulkDelete}
          disabled={selectedIds.length === 0 || isDeleting}
        >
          Delete Selected
        </button>
      </div>

      <div className="organization-table-wrap">
        <table className="organization-table">
          <thead>
            <tr>
              <th className="organization-check-cell">
                <input
                  type="checkbox"
                  aria-label="Select all visible organizations"
                  checked={allVisibleSelected}
                  onChange={toggleSelectAllVisible}
                  disabled={visibleOrganizationIds.length === 0}
                />
              </th>
              <th>Name</th>
              <th>Industry</th>
              <th>Size</th>
              <th>Headquarters</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredOrganizations.length > 0 ? (
              filteredOrganizations.map((org) => {
                const orgId = getOrganizationId(org);
                return (
                  <tr key={orgId}>
                    <td className="organization-check-cell">
                      <input
                        type="checkbox"
                        aria-label={`Select ${org.organizationName || 'organization'}`}
                        checked={selectedIds.includes(orgId)}
                        onChange={() => toggleSelection(orgId)}
                      />
                    </td>
                    <td>
                      <div className="organization-name">
                        <strong>{org.organizationName || 'Untitled organization'}</strong>
                        <span>{org.organization_id || orgId}</span>
                      </div>
                    </td>
                    <td>{org.industry || 'Not specified'}</td>
                    <td>{org.companySize || 0}</td>
                    <td>{org.headquarters || 'Not specified'}</td>
                    <td>
                      <div className="organization-actions">
                        <button className="org-button org-button-secondary" onClick={() => handleEdit(orgId)}>
                          Edit
                        </button>
                        <button
                          className="org-button org-button-danger"
                          onClick={() => handleDelete(org)}
                          disabled={isDeleting}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="6">
                  <div className="organization-empty">No organizations found.</div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {isModalOpen && (
        <div className="organization-modal-overlay">
          <div className="organization-modal-content" role="dialog" aria-modal="true">
            <div className="organization-modal-header">
              <div>
                <p className="organization-eyebrow">Organization profile</p>
                <h3>{isEditing ? 'Edit Organization' : 'Add New Organization'}</h3>
              </div>
              <button className="organization-close-button" onClick={closeModal} aria-label="Close">
                x
              </button>
            </div>

            <form onSubmit={handleSave} className="organization-form">
              <label>
                Name
                <input
                  type="text"
                  value={form.organizationName}
                  onChange={(e) => handleInputChange(e, 'organizationName')}
                  required
                />
              </label>
              <label>
                Industry
                <input
                  type="text"
                  value={form.industry}
                  onChange={(e) => handleInputChange(e, 'industry')}
                  required
                />
              </label>
              <label>
                Company Size
                <input
                  type="number"
                  value={form.companySize}
                  onChange={(e) => handleInputChange(e, 'companySize')}
                  required
                />
              </label>
              <label>
                Business Model
                <input
                  type="text"
                  value={form.businessModel}
                  onChange={(e) => handleInputChange(e, 'businessModel')}
                />
              </label>
              <label>
                Headquarters
                <input
                  type="text"
                  value={form.headquarters}
                  onChange={(e) => handleInputChange(e, 'headquarters')}
                />
              </label>

              <fieldset>
                <legend>Contact Details</legend>
                <label>
                  Name
                  <input
                    type="text"
                    value={form.contactDetails.name}
                    onChange={(e) => handleInputChange(e, 'contactDetails', 'name')}
                  />
                </label>
                <label>
                  Email
                  <input
                    type="email"
                    value={form.contactDetails.email}
                    onChange={(e) => handleInputChange(e, 'contactDetails', 'email')}
                  />
                </label>
                <label>
                  Phone
                  <input
                    type="tel"
                    value={form.contactDetails.phone}
                    onChange={(e) => handleInputChange(e, 'contactDetails', 'phone')}
                  />
                </label>
              </fieldset>

              <fieldset>
                <legend>Financial Details</legend>
                <label>
                  Annual Revenue
                  <input
                    type="number"
                    value={form.financialDetails.annualRevenue}
                    onChange={(e) => handleInputChange(e, 'financialDetails', 'annualRevenue')}
                  />
                </label>
                <label>
                  Funding Stage
                  <input
                    type="text"
                    value={form.financialDetails.fundingStage}
                    onChange={(e) => handleInputChange(e, 'financialDetails', 'fundingStage')}
                  />
                </label>
                <label>
                  Valuation
                  <input
                    type="number"
                    value={form.financialDetails.valuation}
                    onChange={(e) => handleInputChange(e, 'financialDetails', 'valuation')}
                  />
                </label>
              </fieldset>

              <fieldset>
                <legend>Business Details</legend>
                <label>
                  Description
                  <input
                    type="text"
                    value={form.businessDetails.description}
                    onChange={(e) => handleInputChange(e, 'businessDetails', 'description')}
                  />
                </label>
                <label>
                  Other Field
                  <input
                    type="text"
                    value={form.businessDetails.other_field}
                    onChange={(e) => handleInputChange(e, 'businessDetails', 'other_field')}
                  />
                </label>
              </fieldset>

              <div className="organization-form-buttons">
                <button type="button" className="org-button org-button-secondary" onClick={closeModal}>
                  Cancel
                </button>
                <button type="submit" className="org-button org-button-primary">
                  {isEditing ? 'Update' : 'Add'} Organization
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganizationList;
